import React, { useMemo, useState } from 'react';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsCard,
  portalDs,
} from 'src/components/portal-design-system';
import type { UsableError } from 'src/services/error-parsing/error-parsing.types';
import { useGetCreativeImportPreviewQuery } from '../../endpoints/creative-import.endpoints';
import { useCreativeImportProgress } from '../../hooks/use-creative-import-progress';
import type { Client } from '../../types/client.types';
import { formatSkippedBreakdown } from '../../utils/creative-import.utils';
import ImportCreativesModal from './import-creatives-modal';

function getPreviewErrorMessage(error: unknown): { title: string; message: string; isBlocking: boolean } {
  if (!error || typeof error !== 'object') {
    return { title: 'Preview unavailable', message: 'Could not load the creative import preview. Please try again.', isBlocking: false };
  }

  const usable = error as UsableError;
  const responseData = usable.responseData;
  const errorCode = typeof responseData?.error_code === 'string' ? responseData.error_code : null;
  const detail = typeof responseData?.detail === 'string' ? responseData.detail : null;

  if (errorCode === 'flow_sync_not_configured') {
    return {
      title: 'Not configured',
      message: 'Creative import is not configured for this environment (missing sync settings).',
      isBlocking: true,
    };
  }
  if (errorCode === 'upstream_auth_failed') {
    return {
      title: 'Authentication failed',
      message: 'Could not authenticate with the creative import service. Check sync credentials.',
      isBlocking: true,
    };
  }
  if (usable.httpStatus === 429) {
    return {
      title: 'Temporarily limited',
      message: detail ?? 'Preview requests were rate-limited. Wait a moment, then retry.',
      isBlocking: false,
    };
  }
  if (usable.httpStatus === 404) {
    return {
      title: 'Not found',
      message: detail ?? 'The creative import endpoint could not be reached (404).',
      isBlocking: false,
    };
  }
  if (usable.httpStatus && usable.httpStatus >= 500) {
    return {
      title: 'Service unavailable',
      message: detail ?? 'The creative import service is temporarily unavailable. Please try again shortly.',
      isBlocking: false,
    };
  }

  return {
    title: 'Preview unavailable',
    message: detail ?? 'Could not load the creative import preview. Please try again.',
    isBlocking: false,
  };
}

interface ClientStudioSectionProps {
  client: Client;
  drawerOpen: boolean;
}

export const ClientStudioSection: React.FC<ClientStudioSectionProps> = ({
  client,
  drawerOpen,
}) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const isMigrated = Boolean(client.studio_migrated_at);
  const hasStudioWorkspace = Boolean(client.self_service_workspace_id);
  const canLoadPreview = isMigrated && hasStudioWorkspace;

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
    error: previewError,
    refetch: refetchPreview,
  } = useGetCreativeImportPreviewQuery(client.ext_client_id, {
    skip: !drawerOpen || !canLoadPreview,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const previewErrorInfo = useMemo(
    () => (isPreviewError ? getPreviewErrorMessage(previewError) : null),
    [isPreviewError, previewError]
  );

  const {
    status: importStatus,
    isInProgress,
    isFetching: isStatusFetching,
    beginTracking,
  } = useCreativeImportProgress(client.ext_client_id);

  if (!isMigrated) {
    return null;
  }

  const summary = importStatus?.summary;
  const isTerminal = importStatus?.status === 'completed' || importStatus?.status === 'failed';
  const hasPartialOutcome = Boolean(
    summary && (summary.failed > 0 || summary.skipped > 0) && summary.created > 0
  );

  return (
    <>
      <PortalDsCard className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className={portalDs.sectionTitle}>
              Studio
            </h2>
            <p className={`${portalDs.sheetDescription} mt-1`}>
              Manage Studio assets and imports for this client.
            </p>
          </div>
          <PortalDsButton
            variant="secondary"
            size="sm"
            onClick={() => setShowImportModal(true)}
            disabled={!canLoadPreview || isPreviewLoading || (isPreviewError && Boolean(previewErrorInfo?.isBlocking)) || isInProgress}
          >
            Import creatives
          </PortalDsButton>
        </div>

        <div className="mt-4 space-y-3">
          {isPreviewLoading ? (
            <p className={portalDs.bodySm}>
              Loading import preview…
            </p>
          ) : null}

          {isMigrated && !hasStudioWorkspace ? (
            <PortalDsAlert
              variant="danger"
              title="Studio not fully configured"
            >
              Studio has been migrated, but the workspace connection is missing. Creative import
              preview and imports may not work yet.
            </PortalDsAlert>
          ) : null}

          {isPreviewError && hasStudioWorkspace && previewErrorInfo ? (
            <PortalDsAlert
              variant="danger"
              title={previewErrorInfo.title}
            >
              <p>
                {previewErrorInfo.message}
              </p>
              {!previewErrorInfo.isBlocking ? (
                <PortalDsButton
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => void refetchPreview()}
                >
                  Retry
                </PortalDsButton>
              ) : null}
            </PortalDsAlert>
          ) : null}

          {preview && !isPreviewLoading ? (
            <div className="space-y-2 text-sm text-[#5E6278]">
              <p>
                <span className="font-semibold text-[#0D0F1A]">
                  {preview.creative_count}
                </span>
                {' '}
                creatives,
                {' '}
                <span className="font-semibold text-[#0D0F1A]">
                  {preview.variant_count}
                </span>
                {' '}
                variants ready to import from the last 90 days.
              </p>
              <p className={portalDs.hint}>
                Skipped:
                {' '}
                {formatSkippedBreakdown(preview.skipped)}
              </p>
            </div>
          ) : null}

          {isInProgress || importStatus ? (
            <div className="rounded-lg border border-[#EDEEF2] bg-[#FAFAFB] p-3 text-sm text-[#5E6278]">
              {isInProgress ? (
                <p>
                  Import in progress:
                  {' '}
                  <span className="font-semibold text-[#0D0F1A]">
                    {importStatus?.imported_count ?? 0}
                  </span>
                  {importStatus?.creative_count != null ? (
                    <>
                      {' '}
                      of
                      {' '}
                      <span className="font-semibold text-[#0D0F1A]">
                        {importStatus.creative_count}
                      </span>
                    </>
                  ) : null}
                  {' '}
                  creatives imported
                  {isStatusFetching ? '…' : '.'}
                </p>
              ) : null}

              {isTerminal && summary ? (
                <p>
                  Import finished —
                  {' '}
                  <span className="font-semibold text-[#0D0F1A]">
                    {summary.created}
                  </span>
                  {' '}
                  created,
                  {' '}
                  <span className="font-semibold text-[#0D0F1A]">
                    {summary.skipped}
                  </span>
                  {' '}
                  skipped,
                  {' '}
                  <span className="font-semibold text-[#0D0F1A]">
                    {summary.failed}
                  </span>
                  {' '}
                  failed.
                  {hasPartialOutcome ? ' Some creatives could not be imported.' : null}
                </p>
              ) : null}

              {importStatus?.status === 'failed' && !summary ? (
                <p className="text-[#B42318]">
                  Import failed before completion.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </PortalDsCard>

      <ImportCreativesModal
        open={showImportModal}
        client={client}
        preview={preview ?? null}
        onOpenChange={setShowImportModal}
        onImportStarted={beginTracking}
      />
    </>
  );
};

export default ClientStudioSection;
